import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import Navbar from "../../components/layout/Navbar";
import Footer from "../../components/layout/Footer";
import { searchContent, getImageUrl, fetchAllContent, fetchTvStoreCategories } from "../../redux/content";
import { clearSearch } from "../../redux/slice/contentSlice";
import store from "../../redux/store";
import { getTvodProductMap, buildMovieDetailNavState } from "../../utils/tvodMap";
import "../../components/styles/searchPage.scss";

/**
 * SearchPage — Search results screen
 * Figma: 1.1 Search Result (Desktop) (24654:5400) — 1920×950
 *
 * Data flow:
 *   1. Input change → 600ms debounce → pushes ?q= to URL
 *   2. URL ?q= param change → fires searchContent(query) → Redux state
 *   3. searchResults from Redux → rendered as landscape cards
 *   4. Suggestions row = pipe-joined result titles
 *
 * Edge cases:
 *   - Min 2 chars before search fires
 *   - Debounce cancels on fast typing
 *   - URL sync: back/forward navigation restores query
 *   - Clear button resets input + URL + Redux state
 *   - Loading skeleton replaces cards while fetching
 *   - Empty state shown only after a real search (not on initial mount)
 *   - Error state shown on network/server failure
 *   - Redux state cleared on unmount
 *   - Input auto-focused on mount
 */

const SearchPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryFromUrl = searchParams.get("q") || "";
  const [inputValue, setInputValue] = useState(queryFromUrl);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const { searchQuery, searchResults, loaders, errors, categories, moviesByCategories, tvStoreCategories } = useSelector(
    (state) => state.content
  );
  const tvodMapRef = useRef(new Map());

  // Load TVOD product map for rental price detection when navigating to details
  useEffect(() => {
    if (tvStoreCategories.length === 0) {
      fetchTvStoreCategories();
    } else {
      getTvodProductMap(tvStoreCategories).then((map) => { tvodMapRef.current = map; });
    }
  }, [tvStoreCategories]);
  const isLoading = loaders.search;
  const hasError = errors.search;
  const contentLoading = loaders.content;

  // Auto-focus input on mount
  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  // Fetch popular content for pre-search state if not already loaded
  useEffect(() => {
    if (categories.length === 0) {
      console.log("[SearchPage] No content in Redux, fetching for pre-search state");
      fetchAllContent();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // URL param → fire search (handles initial load + back/forward navigation)
  useEffect(() => {
    if (queryFromUrl && queryFromUrl.trim().length >= 2) {
      console.log("[SearchPage] URL param changed, searching:", queryFromUrl);
      searchContent(queryFromUrl);
    }
  }, [queryFromUrl]);

  // Cleanup Redux search state + pending debounce on unmount
  useEffect(() => {
    return () => {
      store.dispatch(clearSearch());
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // Debounced input handler: waits 600ms after last keystroke to update URL
  const handleInputChange = useCallback(
    (value) => {
      setInputValue(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);

      if (value.trim().length >= 2) {
        debounceRef.current = setTimeout(() => {
          console.log("[SearchPage] Debounce fired, pushing query:", value);
          setSearchParams({ q: value });
        }, 600);
      } else if (value.trim().length === 0) {
        // Immediately clear results when input is wiped
        setSearchParams({});
        store.dispatch(clearSearch());
      }
    },
    [setSearchParams]
  );

  // Explicit submit (Enter key / form submit) — skip debounce
  const handleSubmit = (e) => {
    e.preventDefault();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (inputValue.trim().length >= 2) {
      console.log("[SearchPage] Form submitted, query:", inputValue);
      setSearchParams({ q: inputValue });
    }
  };

  // Clear button: reset input, URL, and Redux state
  const handleClear = () => {
    setInputValue("");
    setSearchParams({});
    store.dispatch(clearSearch());
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (inputRef.current) inputRef.current.focus();
    console.log("[SearchPage] Search cleared");
  };

  // Display query: use confirmed Redux query (from last successful search)
  // or fall back to URL param (when search is in-flight or on re-mount)
  const displayQuery = searchQuery || queryFromUrl;

  // Suggestions: pipe-join titles from real results
  const relatedTitles = searchResults
    .map((r) => (typeof r.title === "string" ? r.title : ""))
    .filter(Boolean);

  // Whether a search has been initiated (to differentiate empty state from initial mount)
  const hasSearched = Boolean(displayQuery);

  // Derive popular content for pre-search state:
  // Collect movies from the first two populated categories, cap at 24
  const popularContent = !hasSearched
    ? Object.entries(moviesByCategories)
        .filter(([, movies]) => movies.length > 0)
        .slice(0, 2)
        .flatMap(([, movies]) => movies)
        .slice(0, 24)
    : [];

  // Get the first populated category name for the heading
  const firstCatUid = Object.keys(moviesByCategories).find(
    (uid) => moviesByCategories[uid]?.length > 0
  );
  const popularHeading =
    categories.find((c) => c.uid === firstCatUid)?.name || "Popular on OpenPlay";

  console.log("[SearchPage] query:", displayQuery, "results:", searchResults.length, "loading:", isLoading, "error:", hasError, "popular:", popularContent.length);

  return (
    <div className="search-page">
      <Navbar activeTab="" />

      <div className="search-page__body">

        {/* Search form */}
        <form className="search-page__search-form" onSubmit={handleSubmit}>
          <div className="search-page__search-input-wrapper">
            <svg
              className="search-page__search-icon"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle cx="11" cy="11" r="7" stroke="#737373" strokeWidth="2" />
              <path
                d="M16.5 16.5L20 20"
                stroke="#737373"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            <input
              ref={inputRef}
              type="text"
              className="search-page__search-input"
              placeholder="Search movies, series..."
              value={inputValue}
              onChange={(e) => handleInputChange(e.target.value)}
              autoComplete="off"
              spellCheck="false"
            />
            {inputValue && (
              <button
                type="button"
                className="search-page__clear-btn"
                onClick={handleClear}
                aria-label="Clear search"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M12 4L4 12M4 4L12 12"
                    stroke="#737373"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            )}
          </div>
        </form>

        {/* Pre-search state — popular content grid, shown before any query */}
        {!hasSearched && (
          <section className="search-page__browse">
            <h2 className="search-page__browse-heading">{popularHeading}</h2>
            <div className="search-page__browse-grid">
              {contentLoading &&
                Array.from({ length: 12 }).map((_, i) => (
                  <div
                    key={`bskel-${i}`}
                    className="search-page__browse-card search-page__browse-card--skeleton"
                  >
                    <div className="search-page__skeleton-shimmer" />
                  </div>
                ))
              }
              {!contentLoading && popularContent.map((item, i) => {
                const img = getImageUrl(item, "POSTER") || getImageUrl(item, "BANNER");
                return (
                  <div
                    key={item.id || i}
                    className="search-page__browse-card"
                    onClick={() => {
                      const navState = buildMovieDetailNavState(item, tvodMapRef.current);
                      console.log("[SearchPage] Browse card clicked:", item.id, item.title, "state:", navState);
                      navigate(`/details/${item.id}`, { state: navState });
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") navigate(`/details/${item.id}`, { state: buildMovieDetailNavState(item, tvodMapRef.current) });
                    }}
                    title={item.title || ""}
                  >
                    {img ? (
                      <img
                        src={img}
                        alt={item.title || ""}
                        className="search-page__browse-card-img"
                      />
                    ) : (
                      <div className="search-page__browse-card-placeholder">
                        <span>{item.title || "?"}</span>
                      </div>
                    )}
                  </div>
                );
              })}
              {!contentLoading && popularContent.length === 0 && (
                <p className="search-page__empty">Start typing to search</p>
              )}
            </div>
          </section>
        )}

        {/* Breadcrumb — only shown when a query is active */}
        {displayQuery && (
          <div className="search-page__breadcrumb">
            <span className="search-page__breadcrumb-base">Search result</span>
            <span className="search-page__breadcrumb-sep">&rsaquo;</span>
            <span className="search-page__breadcrumb-query">{displayQuery}</span>
          </div>
        )}

        {/* Error state */}
        {hasError && !isLoading && (
          <p className="search-page__error">
            Something went wrong. Please try again.
          </p>
        )}

        {/* Loading skeletons */}
        {isLoading && (
          <div className="search-page__results">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={`skel-${i}`}
                className="search-page__result-card search-page__result-card--skeleton"
              >
                <div className="search-page__skeleton-shimmer" />
              </div>
            ))}
          </div>
        )}

        {/* Results grid */}
        {!isLoading && searchResults.length > 0 && (
          <div className="search-page__results">
            {searchResults.map((result, i) => {
              const img =
                getImageUrl(result, "POSTER") ||
                getImageUrl(result, "BANNER");
              return (
                <div
                  key={result.id || i}
                  className="search-page__result-card"
                  onClick={() => {
                    const navState = buildMovieDetailNavState(result, tvodMapRef.current);
                    console.log("[SearchPage] Card clicked:", result.id, result.title, "state:", navState);
                    navigate(`/details/${result.id}`, { state: navState });
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") navigate(`/details/${result.id}`, { state: buildMovieDetailNavState(result, tvodMapRef.current) });
                  }}
                >
                  {img ? (
                    <img
                      src={img}
                      alt={result.title || ""}
                      className="search-page__result-img"
                    />
                  ) : (
                    <div className="search-page__result-placeholder" />
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Empty state — only after a search, not on initial mount */}
        {!isLoading && !hasError && hasSearched && searchResults.length === 0 && (
          <p className="search-page__empty">
            No results for &ldquo;{displayQuery}&rdquo;
          </p>
        )}

        {/* Suggestions row — titles from real results */}
        {!isLoading && relatedTitles.length > 0 && (
          <div className="search-page__suggestions">
            <span className="search-page__suggestions-label">
              Explore titles related to :
            </span>
            <span className="search-page__suggestions-titles">
              {relatedTitles.join(" | ")}
            </span>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default SearchPage;
